import React, { useCallback, useState } from "react";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { useAppDispatch } from "@/services/hooks";
import { deletePage } from "@/services/pageServices/pageAction";
import NotificationAlertOptions from "@/layouts/Alerts";
import { asCaught } from "@/services/caughtError";
import { GLOBAL_NOTIFICATION_CONTAINER_ID } from "@/layouts/GlobalNotifications";

/**
 * The "Delete Page Operation" confirm dialog and the delete behind it, as one
 * hook: `confirmDelete(page)` opens the dialog, `confirmElement` is the dialog
 * to render (null while closed). Moved out of DisplayPagesContainer so the
 * Create Page screen's required-pages block asks the same question before it
 * deletes (SCRUM-2021) instead of deleting on the first click.
 *
 * `toHub` is for a screen without a toast container of its own (the Create
 * Page screen while the required-pages block is shown): the success toast then
 * goes to the shell hub's container, and a failure is left to the hub, which
 * already toasts the GET_ERRORS the delete thunk dispatches — toasting it here
 * too would show it twice. Without `toHub` the behaviour is the Pages index's,
 * unchanged.
 */
export default function usePageDeleteConfirm({ toHub = false }: { toHub?: boolean } = {}): {
  confirmDelete: (page: any) => void;
  confirmElement: React.ReactElement | null;
} {
  const dispatch = useAppDispatch();
  const [confirmElement, setConfirmElement] = useState<React.ReactElement | null>(null);

  const hideAlert = useCallback(() => setConfirmElement(null), []);

  const confirmDelete = useCallback(
    (page: any) => {
      // Until SCRUM-1624 this alert had two more shapes — "main page with linked
      // tag pages" and "tag page linked to a main page" — for the tag category
      // pages retired in SCRUM-1565.
      const genericDetails =
        page.type !== undefined
          ? `Are you sure to delete ${page.type} page`
          : `Are you sure to delete page type ${page.page_configuration.type} with route path ${page.page_configuration.path}`;

      setConfirmElement(
        // @ts-expect-error react-bootstrap-sweetalert's prop types reject the valid customIcon (ReactNode) prop this alert passes
        <ReactBSAlert
          custom
          style={{ display: "block", marginTop: "-100px" }}
          title="Delete Page Operation"
          customIcon={
            <div
              className="swal2-icon swal2-question swal2-animate-question-icon text-warning"
              style={{ display: "flex" }}
            >
              <span className="swal2-icon-text">?</span>
            </div>
          }
          onConfirm={async () => {
            hideAlert();
            // Only show success when the delete succeeds; deletePage re-throws on failure
            // (SCRUM-815). The success message is fixed, so the error uses its own text.
            try {
              await dispatch(deletePage(page._id));
              NotificationAlertOptions(
                "success",
                "Page Deleted Successfully",
                toHub ? { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID } : undefined
              );
            } catch (err) {
              if (toHub) return;
              // SCRUM-1296: a refusal that names its referrers is answered by
              // PageDeleteBlockersPanel, which stays on screen. "Please try again"
              // would contradict it — retrying cannot help until the links are gone.
              if (!Array.isArray(asCaught(err)?.response?.data?.referencingPages)) {
                NotificationAlertOptions(
                  "error",
                  asCaught(err)?.response?.data?.message || "Deleting the page failed. Please try again."
                );
              }
            }
          }}
          onCancel={hideAlert}
          confirmBtnBsStyle="warning"
          confirmBtnText="Yes I'm sure"
        >
          <p>{genericDetails}</p>
        </ReactBSAlert>
      );
    },
    [dispatch, hideAlert, toHub]
  );

  return { confirmDelete, confirmElement };
}
